import React, { useState } from 'react';
import { Main, Header, Body } from '../NecttosComp/Layout/Layout';
import { useNecttos } from './context/NecttosContext';
import Button from '../NecttosComp/Button/Button';
import EditWebsite from './EditWebsite';

const WebsiteControlCenter: React.FC = () => {
  const { college, collegeId } = useNecttos();
  const [isEditing, setIsEditing] = useState(false);
  const [websiteStatus] = useState("Published");

  const stats = [
    { label: "Page Views", value: "2,847", change: "+12%", icon: "📈" },
    { label: "Total Pages", value: "8", change: "Recently updated", icon: "📚" },
    { label: "Last Updated", value: "2 hours ago", change: "Auto-synced", icon: "📅" },
    { label: "Active Users", value: "156", change: "+8%", icon: "👥" },
  ];

  const recentActivities = [
    { action: "Updated faculty list", time: "2 hours ago", type: "auto" },
    { action: "New announcement published", time: "5 hours ago", type: "auto" },
    { action: "Homepage banner edited", time: "1 day ago", type: "manual" },
    { action: "Course catalog synchronized", time: "2 days ago", type: "auto" },
  ];

  if (isEditing) {
    return <EditWebsite onBack={() => setIsEditing(false)} />;
  }

  return (
    <Main title="Website Control Center">
      <Header>
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
              <span className="text-white text-sm font-bold">📚</span>
            </div>
            <div>
              <h1 className="text-xl font-semibold text-slate-900">Campus Web Builder</h1>
              <p className="text-sm text-slate-600">{college?.name || 'No College Selected'}</p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <span className={`px-2 py-1 text-xs font-medium rounded-full ${
              websiteStatus === "Published" 
                ? "bg-green-100 text-green-800" 
                : "bg-gray-100 text-gray-800"
            }`}>
              {websiteStatus}
            </span>
          </div>
        </div>
      </Header>
      
      <Body>
        <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
          <main className="max-w-7xl mx-auto px-6 py-8">
            {/* Hero Section */}
            <div className="mb-8">
              <h2 className="text-3xl font-bold text-slate-900 mb-2">
                Website Management Dashboard
              </h2>
              <p className="text-lg text-slate-600">
                Manage your campus website with ease. No technical expertise required.
              </p>
            </div>

            {/* Quick Actions */}
            <div className="grid md:grid-cols-2 gap-6 mb-8">
              <div className="bg-white rounded-lg shadow-sm border border-slate-200 hover:shadow-lg transition-shadow duration-300">
                <div className="p-6 pb-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                      <span className="text-blue-600 text-xl">👁️</span>
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-slate-900">View Website</h3>
                      <p className="text-sm text-slate-600">
                        See your live website as visitors see it
                      </p>
                    </div>
                  </div>
                </div>
                <div className="p-6 pt-0">
                  <div className="w-full">
                    <Button
                      type="view"
                      onClick={() => window.open('#', '_blank')}
                    >
                      <span className="mr-2">👁️</span>
                      View Live Site
                    </Button>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-sm border border-slate-200 hover:shadow-lg transition-shadow duration-300">
                <div className="p-6 pb-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                      <span className="text-green-600 text-xl">✏️</span>
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-slate-900">Edit Website</h3>
                      <p className="text-sm text-slate-600">
                        Customize your website with our visual editor
                      </p>
                    </div>
                  </div>
                </div>
                <div className="p-6 pt-0">
                  <div className="w-full">
                    <Button
                      type="edit"
                      onClick={() => setIsEditing(true)}
                    >
                      <span className="mr-2">✏️</span>
                      Open Editor
                    </Button>
                  </div>
                </div>
              </div>
            </div>

            {/* Statistics */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              {stats.map((stat, index) => (
                <div key={index} className="bg-white rounded-lg shadow-sm border border-slate-200 hover:shadow-md transition-shadow duration-200">
                  <div className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-slate-600 font-medium">{stat.label}</p>
                        <p className="text-2xl font-bold text-slate-900 mt-1">{stat.value}</p>
                        <p className="text-xs text-green-600 mt-1">{stat.change}</p>
                      </div>
                      <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                        <span className="text-slate-600 text-lg">{stat.icon}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Recent Activity */}
            <div className="bg-white rounded-lg shadow-sm border border-slate-200">
              <div className="p-6 pb-4">
                <h3 className="text-xl font-semibold text-slate-900 flex items-center space-x-2">
                  <span>📅</span>
                  <span>Recent Activity</span>
                </h3>
                <p className="text-sm text-slate-600 mt-1">
                  Latest updates and changes to your website
                </p>
              </div>
              <div className="p-6 pt-0">
                <div className="space-y-4">
                  {recentActivities.map((activity, index) => (
                    <div key={index} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                      <div className="flex items-center space-x-3">
                        <div className={`w-2 h-2 rounded-full ${
                          activity.type === 'auto' ? 'bg-green-500' : 'bg-blue-500'
                        }`} />
                        <span className="text-sm text-slate-700">{activity.action}</span>
                      </div>
                      <span className="text-xs text-slate-500">{activity.time}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </main>
        </div>
      </Body>
    </Main>
  );
};

export default WebsiteControlCenter;